/**
 * Credit Block React Query hooks
 * Gobodo only - no mock data.
 * Uses GraphTraversalApi (CreditBlock{suffix} -> Client via credit_block_client_rel) when available; falls back to fetch+join.
 */

import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { getRecordById, getRecordsForObject, updateRecord } from '../recordStorageService';
import { convertFromGobodoRecord } from '../gobodo/gobodoRecordService';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import type { CreditBlockClient, CreditBlockProduct, BureauCredential } from '../../types';
import { mapCreditBlockRecordToProduct } from './creditBlockProductMapper';
import { resolveGraphUserNamesBySfIds, looksLikeGraphUserSfId } from '../gobodo/gobodoUserSfIdLookup';
import {
  makeCacheOpts,
  makeObjectByIdReadCacheOpts,
  makeObjectByKeyReadCacheOpts,
  type GobodoCacheOpts,
} from '../gobodo/gobodoCacheConfig';

const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const CLIENT_OBJECT = `Client${CUSTOM_SUFFIX}`;
const ACCOUNT_MANAGER_COMPANY_REL_FIELD = `AccountManagerCompanyRelField${CUSTOM_SUFFIX}`;
const ACCOUNT_MANAGER_REL_FIELD = `AccountManagerRelField${CUSTOM_SUFFIX}`;
const DAY_TO_DAY_CONTACT_REL_FIELD = `DayToDayContactRelField${CUSTOM_SUFFIX}`;
const REFERRAL_COMPANY_REL_FIELD = `ReferralCompanyRelField${CUSTOM_SUFFIX}`;
const REFERRAL_PARTNER_CONTACT_REL_FIELD = `ReferralPartnerContactRelField${CUSTOM_SUFFIX}`;
const REFERRAL_PERSON_ACCOUNT_REL_FIELD = `ReferralPersonAccountRelField${CUSTOM_SUFFIX}`;
const GRAPH_KEY_SUFFIX_RE = /__(?:stg\d*|stg|dev)_c$|__c$/i;

/**
 * Post-process clients: resolve SF user IDs to display names in systemInfo + accountOwner.
 * Mutates the clients array in-place for efficiency (called right after building them).
 */
async function resolveUserNamesOnClients(clients: CreditBlockClient[]): Promise<void> {
  // Collect all SF user IDs that need resolution
  const sfIds = new Set<string>();
  for (const c of clients) {
    if (looksLikeGraphUserSfId(c.systemInfo.recordCreatedBy)) sfIds.add(c.systemInfo.recordCreatedBy);
    if (looksLikeGraphUserSfId(c.systemInfo.lastModifiedBy)) sfIds.add(c.systemInfo.lastModifiedBy);
    if (looksLikeGraphUserSfId(c.accountOwner)) sfIds.add(c.accountOwner);
  }
  if (sfIds.size === 0) return;

  try {
    const nameMap = await resolveGraphUserNamesBySfIds(Array.from(sfIds));
    for (const c of clients) {
      const createdName = nameMap.get(c.systemInfo.recordCreatedBy);
      if (createdName) c.systemInfo.recordCreatedBy = createdName;

      const modifiedName = nameMap.get(c.systemInfo.lastModifiedBy);
      if (modifiedName) c.systemInfo.lastModifiedBy = modifiedName;

      const ownerName = nameMap.get(c.accountOwner);
      if (ownerName) c.accountOwner = ownerName;
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CreditBlock] Failed to resolve user names:', err);
    }
  }
}

/**
 * Resolve raw SF Account IDs in spouse/primaryParent fields to the related Client's display label.
 * Cached per session — same SF id only fetched once. No-ops when nothing to resolve.
 */
const SF_ACCOUNT_ID_RE = /^001[A-Za-z0-9]{12,15}$/;
const accountNameCache = new Map<string, string>();
const accountManagerCompanyByContactCache = new Map<string, string>();
async function resolveAccountNamesOnClients(clients: CreditBlockClient[]): Promise<void> {
  const sfIds = new Set<string>();
  const managerContactToAccountId = new Map<string, string>();
  const managerContactLookups = new Set<string>();
  const collect = (v: unknown) => {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s && SF_ACCOUNT_ID_RE.test(s) && !accountNameCache.has(s)) sfIds.add(s);
  };
  for (const c of clients) {
    collect((c as unknown as { spouse?: string }).spouse);
    collect((c as unknown as { primaryParent?: string }).primaryParent);
    collect(c.accountManagement?.accountManagerCompany);
    if (!String(c.accountManagement?.accountManagerCompany ?? '').trim()) {
      const managerContact = String((c as unknown as { __accountManagerContactLookup?: string }).__accountManagerContactLookup ?? '').trim();
      if (managerContact && !accountManagerCompanyByContactCache.has(managerContact)) managerContactLookups.add(managerContact);
    }
  }
  if (managerContactLookups.size > 0) {
    try {
      const apiClient = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(apiClient);
      await Promise.all(
        Array.from(managerContactLookups).map(async (contactKey) => {
          try {
            const contact: Record<string, unknown> = await retryWithBackoff(
              () => objectsApi.getObjectByKey(contactKey),
              { maxRetries: 1, initialDelayMs: 500 }
            );
            const d =
              (contact.data as Record<string, unknown> | undefined) ??
              (contact.properties as Record<string, unknown> | undefined) ??
              contact;
            const accountRel = d.AccountRelField__c ?? d[`AccountRelField${CUSTOM_SUFFIX}`];
            const accountLabel = extractLabel(accountRel);
            if (accountLabel) {
              accountManagerCompanyByContactCache.set(contactKey, accountLabel);
              return;
            }
            const accountId = String(d.AccountId__c ?? d.AccountId ?? '').trim();
            if (SF_ACCOUNT_ID_RE.test(accountId)) {
              managerContactToAccountId.set(contactKey, accountId);
              if (!accountNameCache.has(accountId)) sfIds.add(accountId);
            }
          } catch {
            /* leave unresolved */
          }
        })
      );
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CreditBlock] Failed to resolve account manager company contacts:', err);
      }
    }
  }
  if (sfIds.size > 0) {
    try {
      const apiClient = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(apiClient);
      const suffix = CUSTOM_SUFFIX;
      // Look up each SF Account ID by Gobodo key (sfId + suffix). Done in parallel; small fan-out.
      await Promise.all(
        Array.from(sfIds).map(async (sfId) => {
          const key = `${sfId}${suffix}`;
          try {
            const obj: any = await retryWithBackoff(() => objectsApi.getObjectByKey(key), { maxRetries: 1, initialDelayMs: 500 });
            const label = String(obj?.label ?? obj?.data?.Name__c ?? '').trim();
            if (label) accountNameCache.set(sfId, label);
          } catch { /* not found / error — leave unresolved */ }
        })
      );
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CreditBlock] Failed to resolve account names:', err);
      }
    }
  }
  // Apply cache to all clients (some may already have been cached from a previous batch)
  for (const c of clients) {
    const obj = c as unknown as Record<string, unknown>;
    const sp = obj.spouse;
    if (typeof sp === 'string' && SF_ACCOUNT_ID_RE.test(sp.trim())) {
      const name = accountNameCache.get(sp.trim());
      if (name) obj.spouse = name;
    }
    const pp = obj.primaryParent;
    if (typeof pp === 'string' && SF_ACCOUNT_ID_RE.test(pp.trim())) {
      const name = accountNameCache.get(pp.trim());
      if (name) obj.primaryParent = name;
    }
    const company = c.accountManagement?.accountManagerCompany;
    if (typeof company === 'string' && SF_ACCOUNT_ID_RE.test(company.trim())) {
      const name = accountNameCache.get(company.trim());
      if (name) c.accountManagement.accountManagerCompany = name;
    }
    if (!String(c.accountManagement?.accountManagerCompany ?? '').trim()) {
      const contactKey = String((c as unknown as { __accountManagerContactLookup?: string }).__accountManagerContactLookup ?? '').trim();
      const accountId = managerContactToAccountId.get(contactKey);
      const name = accountManagerCompanyByContactCache.get(contactKey) || (accountId ? accountNameCache.get(accountId) : '');
      if (name) c.accountManagement.accountManagerCompany = name;
    }
  }
}
const CREDIT_BLOCK_QUERY_TYPE = `CreditBlock${CUSTOM_SUFFIX}`;
const CLIENT_TYPE_KEY = `Client${CUSTOM_SUFFIX}`;
const CREDIT_BLOCK_CLIENT_REL_KEY = `credit_block_client_rel${CUSTOM_SUFFIX}`;
/** Credit_Bureaus → Client (RDS custom__client), same as rds-sync / setup-gobodo-account-relationships. */
const CREDIT_BUREAUS_CLIENT_REL_KEY = `credit_bureaus_client_rel${CUSTOM_SUFFIX}`;
const BUREAU_OBJECT = `Credit_Bureaus${CUSTOM_SUFFIX}`;

/** Cap for Client / Bureau / CreditBlock list fetches in fallbacks and joins (aligned with recordStorageService default; avoids 100-row bulk pulls). */
export const CREDIT_BLOCK_BULK_LIST_SIZE = 50;

/** Modest server-side cache duration in seconds (gobodo-api-promise expects seconds). 90s to align with table page cache. */
const CREDIT_BLOCK_CACHE_DURATION_SECONDS = 90;
const CREDIT_BLOCK_QUERY_STALE_TIME_MS = 60_000;
const CREDIT_BLOCK_QUERY_GC_TIME_MS = 10 * 60_000;

/** Server-side cache opts for Credit Block / Bureau / Client / Case / Unlock_Request so gobodo-api-promise can cache getObjectsInGraphByType. */
const CREDIT_BLOCK_CACHE_OPTS: GobodoCacheOpts = makeCacheOpts(['credit-block-objects'], CREDIT_BLOCK_CACHE_DURATION_SECONDS);
const BUREAU_CACHE_OPTS: GobodoCacheOpts = makeCacheOpts(['credit-bureau-objects'], CREDIT_BLOCK_CACHE_DURATION_SECONDS);
/** Client object fetch in Credit Block — modest caching so callers hit backend cache (sizes use CREDIT_BLOCK_BULK_LIST_SIZE where capped). */
export const CLIENT_CACHE_OPTS: GobodoCacheOpts = makeCacheOpts(['credit-block-client-objects'], CREDIT_BLOCK_CACHE_DURATION_SECONDS);
/** Case fetches in Credit Block app (Cases tab). */
export const CASE_CACHE_OPTS: GobodoCacheOpts = makeCacheOpts(['credit-block-case-objects'], CREDIT_BLOCK_CACHE_DURATION_SECONDS);
/** Lock/unlock request fetches in Credit Block app (Lock/Unlock Requests tab). */
export const UNLOCK_REQUEST_CACHE_OPTS: GobodoCacheOpts = makeCacheOpts(['credit-block-unlock-request-objects'], CREDIT_BLOCK_CACHE_DURATION_SECONDS);

type GobodoTraversalReadOpts = GobodoCacheOpts & {
  objectSections?: string;
  dataFields?: string;
  maxObjectTypeFields?: number;
  includeExpandedFieldData?: boolean;
  includeRelatedMedia?: boolean;
};

const CLIENT_LIST_TRAVERSAL_DATA_FIELDS = [
  'AccountName__c',
  'Name__c',
  'name',
  'Owner',
  'owner',
  'owner_name__stg3_c',
  'owner_name',
  'AccountOwner__c',
  'Owner__c',
  'OwnerId__c',
  `OwnerId${CUSTOM_SUFFIX}`,
  'OwnerRelField__c',
  'OwnerName__c',
  'Sales_Rep_Name__c',
  'BillingState__c',
  'billingState',
  'Phone__c',
  'phone',
  'PersonMobilePhone__c',
  'personMobilePhone',
  'PersonHomePhone',
  'MobilePhone__c',
  'mobile',
  'SubscriptionStatus__c',
  'subscriptionStatus',
  'Status__c',
  'ArrayClientKey__c',
  'arrayClientKey',
  'custom__array_client_key',
].join(',');

const CREDIT_BLOCK_TABLE_ROW_DATA_FIELDS = [
  'Stage__c',
  `Stage${CUSTOM_SUFFIX}`,
  'stage',
  'custom__stage',
  'SubscriptionStage__c',
].join(',');

const BUREAU_TABLE_ROW_DATA_FIELDS = [
  'Bureau_Name__c',
  'Bureau__c',
  'bureau',
  'Name__c',
  'name',
  'Online_Account_Status__c',
  'Stage__c',
  'Status__c',
  'stage',
  'status',
  'custom__online_account_status',
  'custom__stage',
  'StartDate__c',
  'EnrollmentDate__c',
  'enrollmentDate',
  'custom__start_date',
  'custom__portal_creation_date',
].join(',');

const CREDIT_BLOCK_SUBSCRIPTION_LIST_DATA_FIELDS = [
  'Name__c',
  'name',
  'Subscription_Number__c',
  'SubscriptionName__c',
  'Stage__c',
  `Stage${CUSTOM_SUFFIX}`,
  'stage',
  'custom__stage',
  'SubscriptionStage__c',
  'StartDate__c',
  'Start_Date__c',
  `Start_Date${CUSTOM_SUFFIX}`,
  'startDate',
  'custom__start_date',
  'EnrollmentDate__c',
  'EndDate__c',
  'End_Date__c',
  `End_Date${CUSTOM_SUFFIX}`,
  'endDate',
  'custom__end_date',
  'Open_Cases__c',
  `Open_Cases${CUSTOM_SUFFIX}`,
  'custom__open_cases',
  'Active_Cases_Open__c',
  `Active_Cases_Open${CUSTOM_SUFFIX}`,
  'custom__active_cases_open',
  'ClientRelField__c',
  `ClientRelField${CUSTOM_SUFFIX}`,
  'ClientRelField__c.relatedObject.label',
  'ClientRelField__c.relatedObject.key',
  `ClientRelField${CUSTOM_SUFFIX}.relatedObject.label`,
  `ClientRelField${CUSTOM_SUFFIX}.relatedObject.key`,
  'AccountName__c',
  'ClientName__c',
].join(',');

function buildTraversalProjectionOpts(
  cacheOpts: GobodoCacheOpts,
  dataFields: string,
  includeExpandedFieldData = false
): GobodoTraversalReadOpts {
  return {
    ...cacheOpts,
    objectSections: 'data',
    dataFields,
    maxObjectTypeFields: 0,
    includeExpandedFieldData,
    includeRelatedMedia: false,
  };
}

function buildTraversalObjectSectionsOpts(
  cacheOpts: GobodoCacheOpts,
  objectSections: string
): GobodoTraversalReadOpts {
  return {
    ...cacheOpts,
    objectSections,
    maxObjectTypeFields: 0,
    includeExpandedFieldData: false,
    includeRelatedMedia: false,
  };
}

function parseCreditBlockTraversalCount(raw: unknown): number {
  const obj = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const data = obj?.data != null && typeof obj.data === 'object' ? (obj.data as Record<string, unknown>) : null;
  const candidates = [
    obj?.totalElements,
    obj?.totalCount,
    obj?.total,
    obj?.count,
    data?.totalElements,
    data?.totalCount,
    data?.total,
    data?.count,
    raw,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0) {
      return candidate;
    }
  }
  return 0;
}

/** Cache opts for CreditBlock -> Client traversal (traverseObjects / traverseObjectsCount). */
function creditBlockClientTraversalCacheOpts(
  startIndex?: number,
  count?: number,
  statusFilter?: string,
  searchQuery?: string,
  shape: 'full' | 'table' | 'keys-only' = 'full'
): GobodoCacheOpts {
  const qSeg = searchQuery?.trim() ? `q:${searchQuery.trim()}` : 'no-q';
  return makeCacheOpts(
    ['credit-block-clients-traversal', shape, String(startIndex ?? 0), String(count ?? 0), statusFilter ?? 'all', qSeg],
    CREDIT_BLOCK_CACHE_DURATION_SECONDS
  );
}

function creditBlockClientTraversalCountCacheOpts(statusFilter?: string, searchQuery?: string): GobodoCacheOpts {
  const qSeg = searchQuery?.trim() ? `q:${searchQuery.trim()}` : 'no-q';
  return makeCacheOpts(
    ['credit-block-clients-traversal', 'count-native', statusFilter ?? 'all', qSeg],
    CREDIT_BLOCK_CACHE_DURATION_SECONDS
  );
}
function creditBlockClientCountQueryKey(statusFilter?: string, searchQuery?: string) {
  const qSeg = searchQuery?.trim() ? `q:${searchQuery.trim()}` : 'no-q';
  return ['credit-block', 'clients', 'count', statusFilter ?? 'all', qSeg, CUSTOM_SUFFIX] as const;
}

/** Query key for raw Credit Block + Bureau data (fetched once, filtered per page). */
export const CREDIT_BLOCK_JOIN_DATA_QUERY_KEY = ['credit-block', 'clients', 'join-data', CUSTOM_SUFFIX] as const;

function extractClientRef(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') return raw.trim() || null;
  if (typeof raw === 'object' && raw !== null && 'relatedObject' in raw) {
    const ro = (raw as { relatedObject?: { id?: unknown; key?: unknown } }).relatedObject;
    if (ro?.key != null) return String(ro.key);
    if (ro?.id != null) return String(ro.id);
  }
  if (typeof raw === 'number') return String(raw);
  return null;
}

/** Extract client id/key from a CreditBlock record (relationship skin field or Related_Account__c). */
function getRelatedClientId(cb: Record<string, unknown>): string | null {
  const clientRelKey = `ClientRelField${CUSTOM_SUFFIX}`;
  const data = (cb.data as Record<string, unknown>) ?? cb;
  const raw =
    (cb as { relatedClientId?: unknown }).relatedClientId ??
    data[clientRelKey] ?? data.ClientRelField__c ?? data.Related_Account__c ?? data.custom__related_account ??
    cb[clientRelKey] ?? cb.ClientRelField__c ?? cb.Related_Account__c ?? cb.custom__related_account;
  return extractClientRef(raw);
}

const hasCreditBlockProduct = (r: Record<string, unknown>): boolean => {
  const products = r.Products__c ?? r.products;
  if (Array.isArray(products)) return products.some((p: unknown) => String(p).toLowerCase().includes('credit block'));
  if (typeof products === 'string') return products.toLowerCase().includes('credit block');
  const serviceType = String(r.ServiceType__c ?? r.serviceType ?? '').toLowerCase();
  return serviceType.includes('credit block');
};

const isCreditBlockActive = (cb: Record<string, unknown>): boolean => {
  const stage = String(cb.Stage__c ?? cb.stage ?? cb.custom__stage ?? cb.SubscriptionStage__c ?? 'active').toLowerCase();
  return stage !== 'cancelled' && stage !== 'suspended';
};

const extractLabel = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val === 'string' && val.trim()) return val.trim();
  if (typeof val === 'object' && val !== null && 'label' in val) return String((val as { label?: unknown }).label ?? '');
  if (typeof val === 'object' && val !== null && 'name' in val) return String((val as { name?: unknown }).name ?? '');
  if (typeof val === 'object' && val !== null && 'relatedObject' in val) {
    const ro = (val as { relatedObject?: { label?: unknown; name?: unknown } }).relatedObject;
    return ro ? String(ro.label ?? ro.name ?? '') : '';
  }
  return '';
};

const firstDisplay = (...values: unknown[]): string => {
  for (const value of values) {
    const label = extractLabel(value);
    if (label) return label;
  }
  return '';
};

const getSubscriptionClientRelField = (cb: Record<string, unknown>): unknown => {
  const d = (cb.data as Record<string, unknown>) ?? cb;
  return (
    d[`ClientRelField${CUSTOM_SUFFIX}`] ??
    d.ClientRelField__c ??
    cb[`ClientRelField${CUSTOM_SUFFIX}`] ??
    cb.ClientRelField__c
  );
};

const getSubscriptionClientRelFieldLabel = (cb: Record<string, unknown>): string =>
  extractLabel(getSubscriptionClientRelField(cb));

const getSubscriptionClientRelFieldKey = (cb: Record<string, unknown>): string | null =>
  extractClientRef(getSubscriptionClientRelField(cb));

const getSubscriptionTraversalClientName = (cb: Record<string, unknown>): string => {
  const d = (cb.data as Record<string, unknown>) ?? cb;
  return String(
    d[`CreditBlockClientTravName${CUSTOM_SUFFIX}`] ??
    cb[`CreditBlockClientTravName${CUSTOM_SUFFIX}`] ??
    ''
  ).trim();
};

const getSubscriptionTraversalClientKey = (cb: Record<string, unknown>): string | null => {
  const d = (cb.data as Record<string, unknown>) ?? cb;
  const direct =
    d[`CreditBlockClientTravClientKey${CUSTOM_SUFFIX}`] ??
    cb[`CreditBlockClientTravClientKey${CUSTOM_SUFFIX}`];
  return extractClientRef(direct);
};

const extractSubscriptionClientName = (cb: Record<string, unknown>): string => {
  const d = (cb.data as Record<string, unknown>) ?? cb;
  const relFieldLabel = getSubscriptionClientRelFieldLabel(cb);
  if (relFieldLabel) return relFieldLabel;
  const traversalName = getSubscriptionTraversalClientName(cb);
  if (traversalName) return traversalName;
  const related =
    d.Related_Account__c ??
    d[`Related_Account${CUSTOM_SUFFIX}`] ??
    cb.Related_Account__c ??
    cb[`Related_Account${CUSTOM_SUFFIX}`] ??
    getSubscriptionClientRelField(cb);
  return (
    extractLabel(related) ||
    String(
      d.AccountName__c ??
      d.ClientName__c ??
      d.Account_Name__c ??
      d.accountName ??
      cb.AccountName__c ??
      cb.ClientName__c ??
      cb.Account_Name__c ??
      cb.accountName ??
      ''
    ).trim()
  );
};

const getSubscriptionSubscriberLookup = (cb: Record<string, unknown>): string | null => {
  const relFieldLookup = getSubscriptionClientRelFieldKey(cb);
  if (relFieldLookup) return relFieldLookup;
  const traversalLookup = getSubscriptionTraversalClientKey(cb);
  if (traversalLookup) return traversalLookup;
  const d = (cb.data as Record<string, unknown>) ?? cb;
  const direct =
    d.Related_Account__c ??
    d[`Related_Account${CUSTOM_SUFFIX}`] ??
    cb.Related_Account__c ??
    cb[`Related_Account${CUSTOM_SUFFIX}`];
  const extracted = extractClientRef(direct);
  if (extracted) return extracted;
  return getRelatedClientId(cb);
};

const looksLikeSalesforceAccountId = (value: string): boolean => /^001[a-z0-9]{12,15}$/i.test(String(value).trim());
const looksLikeCreditBlockClientLookup = (value: string): boolean => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return false;
  if (looksLikeSalesforceAccountId(trimmed)) return true;
  const stripped = trimmed.replace(GRAPH_KEY_SUFFIX_RE, '');
  return stripped !== trimmed && looksLikeSalesforceAccountId(stripped);
};

export const __creditBlockSubscriptionTestUtils = {
  getSubscriptionClientRelFieldLabel,
  getSubscriptionClientRelFieldKey,
  getSubscriptionTraversalClientName,
  getSubscriptionTraversalClientKey,
  extractSubscriptionClientName,
  getSubscriptionSubscriberLookup,
  looksLikeCreditBlockClientLookup,
};

const extractSubscriptionLabel = (cb: Record<string, unknown>): string => {
  const d = (cb.data as Record<string, unknown>) ?? cb;
  return String(
    d.Name__c ??
    d.name ??
    d.Subscription_Number__c ??
    d.SubscriptionName__c ??
    cb.Name__c ??
    cb.name ??
    cb.Subscription_Number__c ??
    cb.SubscriptionName__c ??
    ''
  ).trim();
};

/** SF User / Gobodo nested owner object (not always present on Client fetch). */
function extractNestedOwner(val: unknown): string {
  switch (val) {
    case undefined:
    case null:
      return '';
    default:
      break;
  }
  if (typeof val === 'string' && val.trim() === '') return '';
  if (typeof val === 'object' && val !== null && 'relatedObject' in val) return extractLabel(val);
  if (typeof val === 'object' && val !== null) {
    const o = val as Record<string, unknown>;
    const n = o.Name ?? o.name ?? o.FullName ?? o.fullName;
    if (typeof n === 'string' && n.trim()) return n.trim();
    const fn = String(o.FirstName ?? o.firstName ?? '').trim();
    const ln = String(o.LastName ?? o.lastName ?? '').trim();
    if (fn || ln) return `${fn} ${ln}`.trim();
  }
  return '';
}

const mapToCreditBlockClient = (
  r: Record<string, unknown>,
  creditBlocksForClient: Record<string, unknown>[] = [],
  bureausForClient: Record<string, unknown>[] = []
): CreditBlockClient => {
  const contact = (r.Contact__c ?? r.contact) as Record<string, unknown> | undefined;
  const email =
    contact && typeof contact === 'object'
      ? String(contact.email ?? contact.Email__c ?? contact.PersonEmail ?? '')
      : String(
          r.Email__c ?? r.email ?? r.PersonEmail ?? r.PersonEmail__c ?? r.person_email ?? r.ContactEmail__c ?? ''
        );
  const phone =
    contact && typeof contact === 'object'
      ? String(contact.phone ?? contact.Phone__c ?? contact.PersonMobilePhone ?? '')
      : String(
          r.Phone__c ?? r.phone ?? r.PersonMobilePhone ?? r.PersonMobilePhone__c ?? r.PersonHomePhone ?? r.MobilePhone__c ?? ''
        );
  const products = r.Products__c ?? r.products;
  const productList = Array.isArray(products) ? products : typeof products === 'string' ? [products] : [];
  const fromClient: CreditBlockClient['assignedProducts'] = productList
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((name, i) => ({
      id: `prod-${r.id}-${i}`,
      name: String(name).trim(),
      enrollmentDate: new Date().toISOString().slice(0, 10),
      status: 'active' as const,
    }));
  const fromCreditBlocks: CreditBlockClient['assignedProducts'] = creditBlocksForClient.map(
    (cb, i) => mapCreditBlockRecordToProduct(cb, i)
  );
  const assignedProducts =
    fromCreditBlocks.length > 0 ? fromCreditBlocks : fromClient;
  const statusRaw = String(r.SubscriptionStatus__c ?? r.subscriptionStatus ?? r.Status__c ?? 'active').toLowerCase();
  const subscriptionStatus = ['active', 'suspended', 'cancelled', 'inactive'].includes(statusRaw)
    ? (statusRaw as CreditBlockClient['subscriptionStatus'])
    : 'active';
  const d = (r.data as Record<string, unknown>) ?? r;
  const ownerIdSuffixed = `OwnerId${CUSTOM_SUFFIX}`;
  const accountOwner =
    extractNestedOwner(d.Owner ?? r.Owner) ||
    extractNestedOwner(d.owner ?? r.owner) ||
    extractLabel(d.owner_name__stg3_c ?? r.owner_name__stg3_c) ||
    extractLabel(d.owner_name ?? r.owner_name) ||
    extractLabel(d.AccountOwner__c ?? r.AccountOwner__c) ||
    extractLabel(d.Owner__c ?? r.Owner__c) ||
    extractLabel(d.OwnerId__c ?? r.OwnerId__c) ||
    extractLabel(d[ownerIdSuffixed] ?? r[ownerIdSuffixed]) ||
    extractLabel(d.OwnerRelField__c ?? r.OwnerRelField__c) ||
    String(
      d.OwnerName__c ??
        r.OwnerName__c ??
        d.ownerName ??
        r.ownerName ??
        d.Sales_Rep_Name__c ??
        r.Sales_Rep_Name__c ??
        ''
    ).trim() ||
    String(r.accountOwner ?? r.owner ?? r.recordOwner ?? r.createdBy ?? r.owner_id ?? d.owner_id ?? '');
  const accountManagerContactLookup = extractClientRef(
    d[ACCOUNT_MANAGER_REL_FIELD] ??
      r[ACCOUNT_MANAGER_REL_FIELD] ??
      d.AccountManagerRelField__c ??
      r.AccountManagerRelField__c ??
      d.Contact__c ??
      r.Contact__c ??
      d.AccountManager__c ??
      r.AccountManager__c
  );
  const client: CreditBlockClient = {
    id: String((r as { key?: string }).key ?? r.id ?? ''),
    accountName: String(r.AccountName__c ?? r.accountName ?? r.Name__c ?? r.name ?? r.label ?? ''),
    accountOwner: accountOwner || '',
    contact: {
      phone,
      mobile: String(r.PersonMobilePhone__c ?? r.personMobilePhone ?? r.MobilePhone__c ?? r.mobile ?? ''),
      email,
      teamEmail: String(r.Regal_Team_Email__c ?? r.regalTeamEmail ?? r.TeamEmail__c ?? r.custom__regal_team_email ?? ''),
    },
    personalInfo: {
      dateOfBirth: String(r.Birthday__c ?? r.birthday ?? r.PersonBirthDate__c ?? r.dateOfBirth ?? ''),
      socialSecurityNumber: String(r.SSN__c ?? r.ssn ?? r.custom__ssn ?? ''),
    },
    bureauCredentials: (() => {
      try {
        const bc: CreditBlockClient['bureauCredentials'] = {
          chexsystems: {},
          equifax: {},
          experian: {},
          transunion: {},
        };
        for (const b of bureausForClient) {
          const bureau = normalizeBureauName(b.Bureau_Name__c ?? b.Bureau__c ?? b.bureau ?? b.Name__c ?? b.name);
          bc[bureau] = mapBureauRecordToCredential(b);
        }
        return bc;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('[CreditBlock] bureauCredentials mapping error:', e);
        return { chexsystems: {}, equifax: {}, experian: {}, transunion: {} };
      }
    })(),
    arrayInfo: (() => {
      const clientKey = String(
        r.ArrayClientKey__c ?? r.arrayClientKey ?? r.custom__array_client_key ?? ''
      ).trim();
      const failuresRaw = String(
        r.Array_Registration_Failures__c ?? r.arrayRegistrationFailures ?? r.custom__array_registration_failures ?? ''
      ).trim();
      return {
        ...(clientKey ? { clientKey } : {}),
        ...(failuresRaw ? { registrationFailures: failuresRaw.split(',').map((s) => s.trim()).filter(Boolean) } : {}),
      };
    })(),
    addresses: {
      mailing: {
        street: String(r.BillingStreet__c ?? r.billingStreet ?? r.BillingAddress__c ?? ''),
        city: String(r.BillingCity__c ?? r.billingCity ?? ''),
        state: String(r.BillingState__c ?? r.billingState ?? ''),
        zipCode: String(r.BillingPostalCode__c ?? r.billingPostalCode ?? ''),
      },
    },
    accountManagement: {
      // Priority: resolved RelField label (*Name__c, populated by gobodoRecordService converter from
      // the underlying graph relationship) → SF text fields → camelCase fallback.
      managingFirm: firstDisplay(d.Service_Management__c, r.Service_Management__c, d.serviceManagement, r.serviceManagement),
      accountManager: firstDisplay(
        d.AccountManagerName__c,
        r.AccountManagerName__c,
        d[ACCOUNT_MANAGER_REL_FIELD],
        r[ACCOUNT_MANAGER_REL_FIELD],
        d.AccountManagerRelField__c,
        r.AccountManagerRelField__c,
        d.Contact__c,
        r.Contact__c,
        d.AccountManager__c,
        r.AccountManager__c,
        d.accountManager,
        r.accountManager,
        d.AccountOwner__c,
        r.AccountOwner__c
      ),
      accountManagerCompany: firstDisplay(
        d.AccountManagerCompanyName__c,
        r.AccountManagerCompanyName__c,
        d[ACCOUNT_MANAGER_COMPANY_REL_FIELD],
        r[ACCOUNT_MANAGER_COMPANY_REL_FIELD],
        d.AccountManagerCompanyRelField__c,
        r.AccountManagerCompanyRelField__c,
        d.Account_Manager_Company__c,
        r.Account_Manager_Company__c,
        d.accountManagerCompany,
        r.accountManagerCompany
      ),
      dayToDayContact: firstDisplay(
        d.DayToDayContactName__c,
        r.DayToDayContactName__c,
        d[DAY_TO_DAY_CONTACT_REL_FIELD],
        r[DAY_TO_DAY_CONTACT_REL_FIELD],
        d.DayToDayContactRelField__c,
        r.DayToDayContactRelField__c,
        d.Daily_Contact__c,
        r.Daily_Contact__c,
        d.dayToDayContact,
        r.dayToDayContact,
        d.Day_to_Day_Contact__c,
        r.Day_to_Day_Contact__c
      ),
    },
    systemInfo: {
      // Priority: resolved RelField label → enriched name → SF user ID → Gobodo creator ID
      recordCreatedBy: String(r.CreatedByName__c ?? r.created_by_name__stg3_c ?? r.created_by_name ?? r.CreatedById__c ?? r.CreatedBy__c ?? r.createdBy ?? ''),
      lastModifiedBy: String(r.LastModifiedByName__c ?? r.last_modified_by_name__stg3_c ?? r.last_modified_by_name ?? r.LastModifiedById__c ?? r.LastModifiedBy__c ?? r.lastModifiedBy ?? ''),
      createdDate: String(r.CreatedDate__c ?? r.createdDate ?? ''),
      lastModifiedDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? ''),
    },
    // Extended Account fields (picklists, currency, lookups)
    // Spouse / Primary Parent are 'relationship'-typed in Gobodo (not text). Converter populates
    // *Name__c with the related client's display label; raw Spouse__c/Primary_Parent__c is the related key.
    spouse: String(r.SpouseName__c ?? r.Spouse__c ?? r.spouse ?? ''),
    primaryParent: String(r.Primary_Parent_Name__c ?? r.Primary_Parent__c ?? r.primaryParent ?? ''),
    numberOfCollectionAccounts: String(r.Number_of_Collection_Accounts__c ?? r.numberOfCollectionAccounts ?? ''),
    estimatedNetworth: String(r.Estimated_Networth__c ?? r.estimatedNetworth ?? ''),
    annualSalary: String(r.Annual_Salary__c ?? r.annualSalary ?? ''),
    creditReportFlag: String(r.Free_Credit_Report_2__c ?? r.CreditReport__c ?? r.creditReportFlag ?? ''),
    residentialStatus: String(r.Residential_Status__c ?? r.residentialStatus ?? ''),
    residentialAmount: String(r.Residential_Amount__c ?? r.residentialAmount ?? ''),
    employmentStatus: String(r.Employment_Status__c ?? r.employmentStatus ?? ''),
    employer: String(r.Employer__c ?? r.employer ?? ''),
    dataSource: String(r.DataSource__c ?? r.dataSource ?? ''),
    relatedInstitutionalOpportunity: String(r.Related_Institutional_Opportunity__c ?? r.relatedInstitutionalOpportunity ?? ''),
    bulkSmsStatus: String(r.Bulk_SMS_Status__c ?? r.bulkSmsStatus ?? ''),
    bulkEmailStatus: String(r.Bulk_Email_Status__c ?? r.bulkEmailStatus ?? ''),
    referralSourceLocation: String(r.Referral_Source_Location__c ?? r.referralSourceLocation ?? ''),
    leadSource: String(r.Lead_Source__c ?? r.leadSource ?? ''),
    referralCompany: firstDisplay(
      d.ReferralCompanyName__c,
      r.ReferralCompanyName__c,
      d[REFERRAL_COMPANY_REL_FIELD],
      r[REFERRAL_COMPANY_REL_FIELD],
      d.ReferralCompanyRelField__c,
      r.ReferralCompanyRelField__c,
      d.Referral_Company__c,
      r.Referral_Company__c,
      d.ReferralCompany__c,
      r.ReferralCompany__c,
      d.referralCompany,
      r.referralCompany
    ),
    referralCompanyIndustry: String(r.Referral_Company_Industry__c ?? r.referralCompanyIndustry ?? ''),
    referralPartnerContact: firstDisplay(
      d.ReferralPartnerContactName__c,
      r.ReferralPartnerContactName__c,
      d.Referral_Partner_Contact_Name__c,
      r.Referral_Partner_Contact_Name__c,
      d[REFERRAL_PARTNER_CONTACT_REL_FIELD],
      r[REFERRAL_PARTNER_CONTACT_REL_FIELD],
      d.ReferralPartnerContactRelField__c,
      r.ReferralPartnerContactRelField__c,
      d.Referral_Contact__c,
      r.Referral_Contact__c,
      d.ReferralContact__c,
      r.ReferralContact__c,
      d.referralPartnerContact,
      r.referralPartnerContact
    ),
    referralPersonContact: firstDisplay(
      d.ReferralPersonAccountName__c,
      r.ReferralPersonAccountName__c,
      d[REFERRAL_PERSON_ACCOUNT_REL_FIELD],
      r[REFERRAL_PERSON_ACCOUNT_REL_FIELD],
      d.ReferralPersonAccountRelField__c,
      r.ReferralPersonAccountRelField__c,
      d.Person_Referral__c,
      r.Person_Referral__c,
      d.PersonReferral__c,
      r.PersonReferral__c,
      d.referralPersonContact,
      r.referralPersonContact
    ),
    legacyId: String(r.LegacyId__c ?? r.legacyId ?? ''),
    profession: String(r.Profession__c ?? r.profession ?? ''),
    billingConsentDate: String(r.Billing_Consent_Date__c ?? r.billingConsentDate ?? ''),
    sequences: String(r.Sequences__c ?? r.custom__sequences ?? r.sequences ?? ''),
    arrayRegistrationMissingData: String(r.Array_Registration_Missing_Data__c ?? r.arrayRegistrationMissingData ?? ''),
    type: String(r.Type__c ?? r.type ?? r.Account_Type__c ?? r.accountType ?? '').trim(),
    assignedProducts,
    tasks: [],
    notes: (() => {
      const noteBody = String(
        r.Description__c ??
          r.description ??
          r.Notes__c ??
          r.Client_Notes__c ??
          r.Credit_Block_Notes__c ??
          r.custom__description ??
          ''
      ).trim();
      if (!noteBody) return [];
      return [
        {
          id: 'client-note',
          content: noteBody,
          author: String(r.LastModifiedBy__c ?? r.lastModifiedBy ?? '').trim() || '—',
          createdDate: String(r.LastModifiedDate__c ?? r.lastModifiedDate ?? new Date().toISOString()),
          category: 'general' as const,
          isPrivate: false,
        },
      ];
    })(),
    files: [],
    history: [],
    subscriptionStatus,
  };
  if (accountManagerContactLookup) {
    (client as unknown as { __accountManagerContactLookup?: string }).__accountManagerContactLookup = accountManagerContactLookup;
  }
  return client;
};

/**
 * Match creditBlockCaseQueries.toClientGobodoObjectKey (kept here to avoid creditBlockQueries ↔ caseQueries import cycle).
 */
function appendClientGobodoSuffixIfNeeded(raw: string): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const suffix = GobodoConfig.customSuffix();
  if (suffix && s.toLowerCase().endsWith(suffix.toLowerCase())) return s;
  if (/(?:__stg3_c|__stg_c|__dev_c|__c)$/i.test(s)) return s;
  return s + suffix;
}

/**
 * Load one Credit Block client for in-app navigation (Unlock Request “View client”, case/task → client).
 */
export async function fetchCreditBlockClientByLookup(lookup: string): Promise<CreditBlockClient | null> {
  if (!USE_GOBODO_API) return null;
  const raw = String(lookup ?? '').trim();
  if (!raw) return null;
  const suffixed = appendClientGobodoSuffixIfNeeded(raw);
  const tryIds: string[] = [];
  if (suffixed) tryIds.push(suffixed);
  if (raw && raw !== suffixed) tryIds.push(raw);
  for (const id of tryIds) {
    try {
      const rec = await getRecordById(CLIENT_OBJECT, id);
      if (rec) {
        const client = mapToCreditBlockClient(rec as Record<string, unknown>, [], []);
        await resolveUserNamesOnClients([client]);
        await resolveAccountNamesOnClients([client]);
        return client;
      }
    } catch {
      /* try next */
    }
  }
  return null;
}

async function fetchCreditBlockClientListRowByLookup(lookup: string): Promise<CreditBlockClient | null> {
  if (!USE_GOBODO_API) return null;
  const raw = String(lookup ?? '').trim();
  if (!raw) return null;
  const apiClient = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(apiClient);
  const graphId = GobodoConfig.graph();
  const suffixed = appendClientGobodoSuffixIfNeeded(raw);
  const tryIds: string[] = [];
  if (suffixed) tryIds.push(suffixed);
  if (raw && raw !== suffixed) tryIds.push(raw);

  for (const id of tryIds) {
    try {
      const projectionOpts = buildTraversalProjectionOpts(
        /^\d+$/.test(id)
          ? makeObjectByIdReadCacheOpts(Number(id), graphId)
          : makeObjectByKeyReadCacheOpts(id, graphId),
        CLIENT_LIST_TRAVERSAL_DATA_FIELDS
      );
      const gobodoObj = await retryWithBackoff(
        () => (/^\d+$/.test(id) ? objectsApi.getObjectById(Number(id), projectionOpts) : objectsApi.getObjectByKey(id, projectionOpts)),
        { maxRetries: 1, initialDelayMs: 500 }
      );
      const rec = convertFromGobodoRecord(gobodoObj, CLIENT_OBJECT) as Record<string, unknown>;
      const client = mapToCreditBlockClient(rec, [], []);
      return client.id ? client : null;
    } catch {
      /* try next lookup */
    }
  }

  return null;
}

/** Same FK candidates as `creditBlockBureauQueries` clientIdFields (+ Account / legacy SF names) so list join matches detail fetch. */
function getBureauClientId(r: Record<string, unknown>): string | null {
  const clientRelKey = `ClientRelField${CUSTOM_SUFFIX}`;
  const data = (r.data as Record<string, unknown>) ?? r;
  const candidates: unknown[] = [
    (r as { relatedClientId?: unknown }).relatedClientId,
    data.relatedClientId,
    data[clientRelKey],
    r[clientRelKey],
    data.ClientRelField__c,
    r.ClientRelField__c,
    data.Client__c,
    r.Client__c,
    data.AccountRelField__c,
    r.AccountRelField__c,
    data.accountId,
    r.accountId,
    data.custom__client,
    r.custom__client,
    data.Credit_Block_Client__c,
    r.Credit_Block_Client__c,
    data.custom__credit_block_client,
    r.custom__credit_block_client,
    data.Account__c,
    r.Account__c,
    data.Related_Account__c,
    r.Related_Account__c,
    data.custom__related_account,
    r.custom__related_account,
  ];
  for (const raw of candidates) {
    const id = extractClientRef(raw);
    if (id) return id;
  }
  return null;
}

const BUREAUS = ['chexsystems', 'equifax', 'experian', 'transunion'] as const;

function normalizeBureauName(val: unknown): (typeof BUREAUS)[number] {
  const s = String(val ?? '').toLowerCase();
  if (s.includes('chex')) return 'chexsystems';
  if (s.includes('equifax')) return 'equifax';
  if (s.includes('experian')) return 'experian';
  if (s.includes('transunion') || s.includes('trans union')) return 'transunion';
  return 'equifax';
}

function mapBureauRecordToCredential(r: Record<string, unknown>): BureauCredential {
  const data = (r.data as Record<string, unknown>) ?? r;
  const statusRaw = String(
    data.Online_Account_Status__c ?? data.Stage__c ?? data.Status__c ?? data.stage ?? data.status ?? data.custom__online_account_status ?? data.custom__stage ?? ''
  ).trim().toLowerCase();
  const statusMap: Record<string, BureauCredential['status']> = {
    locked: 'locked', unlocked: 'unlocked', monitored: 'monitored', unknown: 'unknown',
    blocked: 'locked', unblocked: 'unlocked',
    active: 'unlocked', enrolled: 'unlocked', registered: 'unlocked',
    inactive: 'locked', suspended: 'locked',
  };
  const status = statusMap[statusRaw] ?? (['locked', 'unlocked', 'monitored', 'unknown'].includes(statusRaw) ? (statusRaw as BureauCredential['status']) : undefined);
  const unblockStatusRaw = String(data.UnblockRequestStatus__c ?? data.unblockRequestStatus ?? '').toLowerCase();
  const unblockRequestStatus = ['pending', 'approved', 'denied', 'completed'].includes(unblockStatusRaw)
    ? (unblockStatusRaw as BureauCredential['unblockRequestStatus'])
    : undefined;
  const authorityRaw = String(data.Authority__c ?? data.authority ?? r.Authority__c ?? '').toLowerCase();
  const authority = authorityRaw === 'primary' ? 'primary' : authorityRaw === 'secondary' ? 'secondary' : undefined;
  const startVal = data.StartDate__c ?? data.EnrollmentDate__c ?? data.enrollmentDate ?? data.custom__start_date ?? data.custom__portal_creation_date ?? r.StartDate__c ?? r.EnrollmentDate__c;
  const endVal = data.EndDate__c ?? data.UnblockDate__c ?? data.unblockDate ?? data.custom__end_date ?? r.EndDate__c ?? r.UnblockDate__c;
  const permUnblockVal = data.PermUnblockDate__c ?? data.permUnblockDate ?? r.PermUnblockDate__c;
  const portalCreationVal = data.PortalCreationDate__c ?? data.portalCreationDate ?? data.custom__portal_creation_date ?? r.PortalCreationDate__c;
  const pinMailedVal =
    data.PIN_Mailed__c ??
    data.PINsMailed__c ??
    data.pinMailed ??
    r.PIN_Mailed__c ??
    r.PINsMailed__c;
  const blockProc =
    data.Block_Process_Collection__c ?? data.blockProcessCollection ?? r.Block_Process_Collection__c;
  const unblockProc =
    data.Unblock_Process_Collection__c ?? data.unblockProcessCollection ?? r.Unblock_Process_Collection__c;
  const tri = (v: unknown): boolean | undefined => {
    if (v === true || v === 'true' || v === 1) return true;
    if (v === false || v === 'false' || v === 0) return false;
    return undefined;
  };
  const x4 =
    data.X4_Digit_PIN__c ?? data.x4DigitPin ?? r.X4_Digit_PIN__c ?? data.FourDigitPIN__c ?? data.fourDigitPIN;
  return {
    creditBureauId: String(r.id ?? r.key ?? data.Id ?? '').trim() || undefined,
    authority,
    status,
    portalCreationDate: String(portalCreationVal ?? '').trim().slice(0, 10) || undefined,
    enrollmentDate: String(startVal ?? '').trim().slice(0, 10) || undefined,
    startDate: String(data.StartDate__c ?? data.startDate ?? r.StartDate__c ?? '').trim().slice(0, 10) || undefined,
    unblockDate: String(endVal ?? '').trim().slice(0, 10) || undefined,
    endDate: String(data.EndDate__c ?? data.endDate ?? r.EndDate__c ?? '').trim().slice(0, 10) || undefined,
    permUnblockDate: String(permUnblockVal ?? '').trim().slice(0, 10) || undefined,
    unblockRequestStatus,
    username: String(data.Username__c ?? data.UserName__c ?? data.username ?? r.Username__c ?? r.UserName__c ?? '').trim() || undefined,
    password: String(data.Password__c ?? data.password ?? r.Password__c ?? '').trim() || undefined,
    pinMailed: pinMailedVal === true || pinMailedVal === 'true' || pinMailedVal === 1,
    pin: String(data.Pin__c ?? data.PIN__c ?? data.pin ?? r.Pin__c ?? r.PIN__c ?? '').trim() || undefined,
    challengeQuestion: String(data.ChallengeQuestion__c ?? data.challengeQuestion ?? r.ChallengeQuestion__c ?? '').trim() || undefined,
    challengeAnswer: String(x4 ?? data.challengeAnswer ?? r.challengeAnswer ?? '').trim() || undefined,
    notes: String(data.Notes__c ?? data.notes ?? r.Notes__c ?? r.notes ?? '').trim() || undefined,
    accountAlias: String(data.Account_Alias__c ?? data.accountAlias ?? r.Account_Alias__c ?? '').trim() || undefined,
    blockProcessCollection: tri(blockProc),
    unblockProcessCollection: tri(unblockProc),
    requestNotes: String(data.Request__c ?? data.requestNotes ?? r.Request__c ?? '').trim() || undefined,
  };
}

/** Fetch distinct Credit Block client IDs (for filtering cases). Uses a key-only traversal when possible. */
export async function getCreditBlockClientIds(): Promise<string[]> {
  try {
    const clientKeys = await getDistinctClientTraversalKeys();
    if (clientKeys.length > 0) {
      return clientKeys;
    }
  } catch {
    // fall through to fetch
  }
  const creditBlockRaw = await getRecordsForObject(CREDIT_BLOCK_QUERY_TYPE, {
    size: CREDIT_BLOCK_BULK_LIST_SIZE,
    cache: CREDIT_BLOCK_CACHE_OPTS,
  }).catch(() => [] as Record<string, unknown>[]);
  const ids = new Set<string>();
  for (const cb of creditBlockRaw as Record<string, unknown>[]) {
    if (!isCreditBlockActive(cb)) continue;
    const cid = getRelatedClientId(cb);
    if (cid) ids.add(cid.replace(GRAPH_KEY_SUFFIX_RE, ''));
  }
  return Array.from(ids);
}

/** Lightweight hook for Credit Block client IDs (e.g. Cases tab filtering). Uses traversal when possible. */
export function useCreditBlockClientIds(enableQuery = true) {
  return useQuery({
    queryKey: ['credit-block', 'client-ids', CUSTOM_SUFFIX],
    queryFn: getCreditBlockClientIds,
    enabled: USE_GOBODO_API && enableQuery,
    staleTime: 60_000,
  });
}

const stripSuffixForMap = (s: string) => (s ?? '').replace(GRAPH_KEY_SUFFIX_RE, '');

/**
 * Fetch Client records and build id/key -> accountName map (capped batch).
 * Not wired from React Query hooks in-app; kept for scripts, future features, or ad-hoc callers.
 */
export async function fetchClientNamesMap(): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  const records = await getRecordsForObject(CLIENT_OBJECT, {
    size: CREDIT_BLOCK_BULK_LIST_SIZE,
    cache: CLIENT_CACHE_OPTS,
  }).catch(() => [] as Record<string, unknown>[]);
  for (const r of records as Record<string, unknown>[]) {
    const key = String((r as { key?: string }).key ?? r.id ?? '');
    const data = (r as { data?: Record<string, unknown> }).data ?? r;
    const name = String(
      r.AccountName__c ?? r.accountName ?? r.Name__c ?? r.name ?? r.label ??
      (data && typeof data === 'object' ? (data as Record<string, unknown>).Name__c ?? (data as Record<string, unknown>).name : '')
    ).trim();
    if (!name) continue;
    out[key] = name;
    const norm = stripSuffixForMap(key);
    if (norm) out[norm] = name;
  }
  return out;
}

const isRetryableError = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number }; status?: number })?.response?.status
    ?? (err as { status?: number })?.status;
  if (status === 504 || (status && status >= 500)) return false;
  return true;
};

export function useCreditBlockClients(enableQuery = true) {
  return useQuery({
    queryKey: ['credit-block', 'clients', CUSTOM_SUFFIX],
    queryFn: async (): Promise<CreditBlockClient[]> => {
      try {
        const clientRecs = await getAllClientsViaTraversal();
        if (clientRecs.length === 0) {
          const count = await getClientsTraversalCount().catch(() => 0);
          if (count === 0) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CreditBlock] Traversal returned 0 clients (no bulk Client__* fetch fallback).');
            }
            return [];
          }
        }
        const { clientIdToCreditBlocks, clientIdToBureaus } = await fetchAndJoinCreditBlocksBureaus(clientRecs);
        const hasActiveSubscription = (r: Record<string, unknown>) => {
          const key = String((r as { key?: string }).key ?? r.id ?? '');
          const norm = stripSuffixForJoin(key);
          const cbs = clientIdToCreditBlocks.get(norm) ?? [];
          return hasCreditBlockProduct(r) || cbs.some(isCreditBlockActive);
        };
        const filteredRecs = clientRecs.filter(hasActiveSubscription);
        const sourceRecs = filteredRecs.length > 0 ? filteredRecs : clientRecs;
        if (process.env.NODE_ENV === 'development' && clientRecs.length > 0 && filteredRecs.length === 0) {
          console.warn('[CreditBlock] traversal CB filter matched 0 — using all', clientRecs.length, 'traversal rows');
        }
        const out: CreditBlockClient[] = [];
        for (const r of sourceRecs) {
          try {
            const key = String((r as { key?: string }).key ?? r.id ?? '');
            const norm = stripSuffixForJoin(key);
            out.push(
              mapToCreditBlockClient(
                r,
                clientIdToCreditBlocks.get(norm) ?? [],
                clientIdToBureaus.get(norm) ?? []
              )
            );
          } catch (rowErr) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CreditBlock] skip client row map', rowErr);
            }
          }
        }
        // Resolve SF user IDs to display names for systemInfo fields
        await resolveUserNamesOnClients(out);
        await resolveAccountNamesOnClients(out);
        return out;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CreditBlock] Full client list query failed (no bulk Client__* fetch fallback).', e);
        }
        try {
          const fallback = await getRecordsForObject(CLIENT_OBJECT, {
            size: CREDIT_BLOCK_BULK_LIST_SIZE,
            cache: CLIENT_CACHE_OPTS,
          });
          const fallbackClients = (fallback as Record<string, unknown>[])
            .map((r) => mapToCreditBlockClient(r, [], []))
            .filter((c) => !!(c.id && String(c.id).trim()));
          await resolveUserNamesOnClients(fallbackClients);
          await resolveAccountNamesOnClients(fallbackClients);
          return fallbackClients;
        } catch {
          return [];
        }
      }
    },
    enabled: USE_GOBODO_API && enableQuery,
    staleTime: 60_000,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
    throwOnError: false,
  });
}

export type CreditBlockSubscriptionListRow = CreditBlockProduct & {
  subscriptionLabel?: string;
  clientName?: string;
  clientLookupId?: string;
};

export interface CreditBlockSubscriptionsPaginatedResult {
  subscriptions: CreditBlockSubscriptionListRow[];
  totalCount: number;
}

/** Server-side cache opts for the current Credit Blocks tab page fetch. */
function subscriptionPageCacheOpts(page: number, pageSize: number, statusFilter: 'active' | 'all'): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-subscriptions-traversal', 'page', String(page), String(pageSize), statusFilter, CUSTOM_SUFFIX],
    CREDIT_BLOCK_CACHE_DURATION_SECONDS,
  );
}

function subscriptionCountCacheOpts(statusFilter: 'active' | 'all'): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-subscriptions-traversal', 'count', statusFilter, CUSTOM_SUFFIX],
    CREDIT_BLOCK_CACHE_DURATION_SECONDS,
  );
}

function creditBlockSubscriptionsPageQueryKey(page: number, pageSize: number, statusFilter: 'active' | 'all') {
  return ['credit-block', 'subscriptions', 'paginated', pageSize, page, statusFilter, CUSTOM_SUFFIX] as const;
}

function creditBlockSubscriptionsCountQueryKey(statusFilter: 'active' | 'all') {
  return ['credit-block', 'subscriptions', 'count', statusFilter, CUSTOM_SUFFIX] as const;
}

function buildCreditBlockSubscriptionsTraversalInput(
  statusFilter: 'active' | 'all',
  pagination?: { startIndex?: number; count?: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([CREDIT_BLOCK_QUERY_TYPE]);
  input.setEndObjectTypes([CREDIT_BLOCK_QUERY_TYPE]);
  input.setMaxPathLength(0);
  const stageFilters = buildCreditBlockStageFilters(statusFilter === 'all' ? undefined : statusFilter);
  if (stageFilters.length > 0) {
    try {
      input.setStartObjectFieldFilters(stageFilters);
    } catch {
      // Backend may not support startObjectFieldFilters on zero-hop traversals.
    }
  }
  if (pagination?.startIndex != null && pagination.startIndex >= 0) input.setStartIndex(pagination.startIndex);
  if (pagination?.count != null && pagination.count > 0) input.setCount(pagination.count);
  return input;
}

function convertCreditBlockSubscriptionRecord(record: unknown): Record<string, unknown> {
  const rawRecord = (record && typeof record === 'object' ? record : {}) as Record<string, unknown>;
  const converted = convertFromGobodoRecord(rawRecord, CREDIT_BLOCK_QUERY_TYPE) as Record<string, unknown>;
  const rawData =
    (rawRecord.data && typeof rawRecord.data === 'object' ? rawRecord.data : undefined) ??
    (rawRecord.properties && typeof rawRecord.properties === 'object' ? rawRecord.properties : undefined);
  if (!rawData) return converted;
  return {
    ...converted,
    data: rawData as Record<string, unknown>,
  };
}

async function fetchCreditBlockSubscriptionsTraversalPage(
  page: number,
  pageSize: number,
  statusFilter: 'active' | 'all',
): Promise<Record<string, unknown>[]> {
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const startIndex = Math.max(0, (page - 1) * pageSize);
  const input = buildCreditBlockSubscriptionsTraversalInput(statusFilter, { startIndex, count: pageSize });
  const raw = await retryWithBackoff(
    () =>
      traversalApi.traverseObjects(
        input,
        buildTraversalProjectionOpts(
          subscriptionPageCacheOpts(page, pageSize, statusFilter),
          CREDIT_BLOCK_SUBSCRIPTION_LIST_DATA_FIELDS,
          true,
        ),
      ),
    { maxRetries: 2, initialDelayMs: 1000 },
  );
  if (!Array.isArray(raw)) return [];
  return raw.map((record) => convertCreditBlockSubscriptionRecord(record));
}

async function fetchCreditBlockSubscriptionsTraversalCount(
  statusFilter: 'active' | 'all',
): Promise<number> {
  const traversalApi = new GobodoApi.GraphTraversalApi(getGobodoClient());
  const input = buildCreditBlockSubscriptionsTraversalInput(statusFilter);
  const raw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(input, subscriptionCountCacheOpts(statusFilter)),
    { maxRetries: 2, initialDelayMs: 1000 },
  );
  return parseCreditBlockTraversalCount(raw);
}

/**
 * A row is "active" if creditBlockStageToStatus normalized it to 'active'.
 * mapCreditBlockRecordToProduct already runs that normalization for every
 * Stage__c value (and falls back to 'active' when Stage is missing), so we
 * just trust the resulting `status` rather than re-deriving from raw stage.
 */
function isActiveSubscriptionRow(row: CreditBlockSubscriptionListRow): boolean {
  return String(row.status ?? '').trim().toLowerCase() === 'active';
}

/**
 * Paginated Credit Block subscriptions.
 *
 * Strategy: fetch only the requested server-side page via a zero-hop
 * CreditBlock traversal, resolve exact totalCount in parallel, and prefetch
 * the next page opportunistically. This keeps first render on the critical
 * path to just the visible page.
 *
 * statusFilter:
 *   - 'active' (default): hide cancelled/inactive/suspended subs
 *   - 'all'             : show every CreditBlock__c record
 */
export function useCreditBlockSubscriptionsPaginated(
  page: number,
  pageSize: number,
  enableQuery = true,
  statusFilter: 'active' | 'all' = 'active',
) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: creditBlockSubscriptionsPageQueryKey(page, pageSize, statusFilter),
    queryFn: async (): Promise<CreditBlockSubscriptionsPaginatedResult> => {
      const runLegacyFallback = async (): Promise<CreditBlockSubscriptionsPaginatedResult> => {
        const objectsApi = new GobodoApi.GraphObjectsApi(getGobodoClient());
        const rawResponse = await retryWithBackoff(
          () =>
            objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), CREDIT_BLOCK_QUERY_TYPE, {
              page: Math.max(0, page - 1),
              size: pageSize,
              ...buildTraversalProjectionOpts(
                subscriptionPageCacheOpts(page, pageSize, statusFilter),
                CREDIT_BLOCK_SUBSCRIPTION_LIST_DATA_FIELDS,
                true,
              ),
            }),
          { maxRetries: 2, initialDelayMs: 1000 },
        ).catch(() => [] as unknown[]);
        const rawArray = Array.isArray(rawResponse)
          ? rawResponse
          : Array.isArray((rawResponse as { content?: unknown[] }).content)
            ? ((rawResponse as { content?: unknown[] }).content ?? [])
            : Array.isArray((rawResponse as { items?: unknown[] }).items)
              ? ((rawResponse as { items?: unknown[] }).items ?? [])
              : [];
        const totalCountFallback = parseCreditBlockTraversalCount(rawResponse);
        const raw = rawArray.map((record) => convertCreditBlockSubscriptionRecord(record));
        const subscriptions = await mapCreditBlockSubscriptionRows(raw);
        const filtered = statusFilter === 'all' ? subscriptions : subscriptions.filter(isActiveSubscriptionRow);
        const inferredTotal =
          filtered.length === pageSize
            ? Math.max(page * pageSize + 1, totalCountFallback || 0)
            : Math.max((page - 1) * pageSize + filtered.length, totalCountFallback || 0);
        return { subscriptions: filtered, totalCount: totalCountFallback || inferredTotal };
      };

      try {
        const totalCountPromise = queryClient.fetchQuery({
          queryKey: creditBlockSubscriptionsCountQueryKey(statusFilter),
          queryFn: () => fetchCreditBlockSubscriptionsTraversalCount(statusFilter),
          staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
          gcTime: CREDIT_BLOCK_QUERY_GC_TIME_MS,
          retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
        });
        const [raw, totalCount] = await Promise.all([
          fetchCreditBlockSubscriptionsTraversalPage(page, pageSize, statusFilter),
          totalCountPromise,
        ]);
        const subscriptions = await mapCreditBlockSubscriptionRows(raw);
        if (totalCount > page * pageSize) {
          void queryClient.prefetchQuery({
            queryKey: creditBlockSubscriptionsPageQueryKey(page + 1, pageSize, statusFilter),
            queryFn: async (): Promise<CreditBlockSubscriptionsPaginatedResult> => {
              const [nextRaw] = await Promise.all([
                fetchCreditBlockSubscriptionsTraversalPage(page + 1, pageSize, statusFilter),
                Promise.resolve(totalCount),
              ]);
              const nextSubscriptions = await mapCreditBlockSubscriptionRows(nextRaw);
              return { subscriptions: nextSubscriptions, totalCount };
            },
            staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
            gcTime: CREDIT_BLOCK_QUERY_GC_TIME_MS,
            retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
          }).catch(() => undefined);
        }
        return { subscriptions, totalCount };
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CreditBlock] subscriptions traversal pagination failed; falling back to object page fetch', error);
        }
        return runLegacyFallback();
      }
    },
    enabled: USE_GOBODO_API && enableQuery && page >= 1 && pageSize >= 1,
    staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
    gcTime: CREDIT_BLOCK_QUERY_GC_TIME_MS,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
    throwOnError: false,
  });
}

async function mapCreditBlockSubscriptionRows(
  raw: Record<string, unknown>[],
): Promise<CreditBlockSubscriptionListRow[]> {
  return raw
    .filter((cb) => {
      const key = String((cb as { key?: unknown }).key ?? '').trim();
      return key ? key.endsWith(CUSTOM_SUFFIX) : true;
    })
    .map((cb, idx) => {
      const relatedClientId = getSubscriptionSubscriberLookup(cb) ?? '';
      const labelName = extractSubscriptionClientName(cb);
      const fallbackName = labelName && looksLikeCreditBlockClientLookup(labelName) ? '' : labelName;
      const subscriptionLabel = extractSubscriptionLabel(cb);
      return {
        ...mapCreditBlockRecordToProduct(cb, idx),
        subscriptionLabel: subscriptionLabel || undefined,
        clientName: fallbackName || undefined,
        clientLookupId: relatedClientId || undefined,
      };
    });
}

const STATUS_FILTER_VALUES = ['active', 'suspended', 'cancelled', 'inactive'] as const;
const CANCELLED_STAGE_VALUES = ['Cancelled', 'cancelled', 'Canceled', 'canceled'];
const SUSPENDED_STAGE_VALUES = ['Suspended', 'suspended', 'NSF', 'Minor On-Hold', 'Deceased'];

function buildClientSubscriptionStatusFilter(statusFilter?: string): InstanceType<typeof GobodoApi.IndexFilter> | null {
  if (!statusFilter || !STATUS_FILTER_VALUES.includes(statusFilter as typeof STATUS_FILTER_VALUES[number])) return null;
  const statusFilterObj = new GobodoApi.IndexFilter();
  statusFilterObj.setIndexName('SubscriptionStatus__c');
  statusFilterObj.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
  statusFilterObj.setValues([statusFilter]);
  return statusFilterObj;
}

function buildStageFilter(
  fn: typeof GobodoApi.IndexFilter.FunctionEnum.EQUALS | typeof GobodoApi.IndexFilter.FunctionEnum.NOT_EQUALS,
  values: string[]
): InstanceType<typeof GobodoApi.IndexFilter> {
  const filter = new GobodoApi.IndexFilter();
  filter.setIndexName('Stage__c');
  filter.setFunction(fn);
  filter.setValues(values);
  return filter;
}

function buildCreditBlockStageFilters(statusFilter?: string): Array<InstanceType<typeof GobodoApi.IndexFilter>> {
  switch (statusFilter) {
    case 'cancelled':
      return [buildStageFilter(GobodoApi.IndexFilter.FunctionEnum.EQUALS, CANCELLED_STAGE_VALUES)];
    case 'suspended':
      return [buildStageFilter(GobodoApi.IndexFilter.FunctionEnum.EQUALS, SUSPENDED_STAGE_VALUES)];
    case 'active':
      return [buildStageFilter(GobodoApi.IndexFilter.FunctionEnum.NOT_EQUALS, [...CANCELLED_STAGE_VALUES, ...SUSPENDED_STAGE_VALUES])];
    default:
      return [];
  }
}

function applyClientSearch(
  input: InstanceType<typeof GobodoApi.GraphTraversalInput>,
  graphId: number,
  searchQuery?: string
): void {
  const q = searchQuery?.trim();
  if (!q) return;
  try {
    input.setSearchQuery(q);
    input.setSearchGraphId(graphId);
  } catch {
    // Backend may not support traversal search
  }
}

function dedupeClientTraversalRecords(records: Record<string, unknown>[]): Record<string, unknown>[] {
  const byClientId = new Map<string, Record<string, unknown>>();
  for (const record of records) {
    const dedupeKey = getClientTraversalDedupeKey(record);
    if (!dedupeKey || byClientId.has(dedupeKey)) continue;
    byClientId.set(dedupeKey, record);
  }
  return Array.from(byClientId.values());
}

function getClientTraversalDedupeKey(record: Record<string, unknown>): string {
  const key = String((record as { key?: string }).key ?? record.id ?? '').trim();
  return stripSuffixForJoin(key) || key;
}

/** Build base GraphTraversalInput for CreditBlock -> Client traversal (no pagination). */
function buildCreditBlockTraversalInput(
  statusFilter?: string,
  searchQuery?: string,
  pagination?: { startIndex?: number; count?: number }
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  const graphId = GobodoConfig.graph();
  input.setGraphId(graphId);
  input.setStartObjectTypes([CREDIT_BLOCK_QUERY_TYPE]);
  input.setEndObjectTypes([CLIENT_TYPE_KEY]);
  input.setRelationshipTypes([CREDIT_BLOCK_CLIENT_REL_KEY]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.OUTGOING);
  input.setMaxPathLength(1);
  const stageFilters = buildCreditBlockStageFilters(statusFilter);
  if (stageFilters.length > 0) {
    try {
      input.setStartObjectFieldFilters(stageFilters);
    } catch {
      // Backend may not support startObjectFieldFilters; row status still derives from CreditBlock data.
    }
  }
  if (statusFilter === 'inactive') {
    const statusFilterObj = buildClientSubscriptionStatusFilter(statusFilter);
    if (statusFilterObj) {
      try {
        input.setEndObjectFieldFilters([statusFilterObj]);
      } catch {
        // Backend may not support endObjectFieldFilters
      }
    }
  }
  if (pagination?.startIndex != null && pagination.startIndex >= 0) input.setStartIndex(pagination.startIndex);
  if (pagination?.count != null && pagination.count > 0) input.setCount(pagination.count);
  applyClientSearch(input, graphId, searchQuery);
  return input;
}

/**
 * Fetch a page of client records by traversing from CreditBlock{suffix} to Client via credit_block_client_rel.
 * Uses startIndex and count on the traversal input for server-side pagination. Optional statusFilter filters CreditBlock stages.
 */
async function getClientsViaTraversal(
  startIndex?: number,
  count?: number,
  statusFilter?: string,
  searchQuery?: string,
  opts?: {
    projectionDataFields?: string;
    cacheShape?: 'full' | 'table';
  }
): Promise<Record<string, unknown>[]> {
  const out = await getClientsViaTraversalRaw(startIndex, count, statusFilter, searchQuery, opts);
  return dedupeClientTraversalRecords(out);
}

async function getClientsViaTraversalRaw(
  startIndex?: number,
  count?: number,
  statusFilter?: string,
  searchQuery?: string,
  opts?: {
    projectionDataFields?: string;
    objectSections?: string;
    cacheShape?: 'full' | 'table' | 'keys-only';
  }
): Promise<Record<string, unknown>[]> {
  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const input = buildCreditBlockTraversalInput(statusFilter, searchQuery, { startIndex, count });

  const cacheOpts = creditBlockClientTraversalCacheOpts(
    startIndex,
    count,
    statusFilter,
    searchQuery,
    opts?.cacheShape ?? 'full'
  );
  const traversalOpts = opts?.projectionDataFields
    ? buildTraversalProjectionOpts(cacheOpts, opts.projectionDataFields)
    : opts?.objectSections
      ? buildTraversalObjectSectionsOpts(cacheOpts, opts.objectSections)
      : cacheOpts;
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, traversalOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  if (!result || !Array.isArray(result)) return [];

  // CB-103/CB-104 investigation: log raw traversal results when search is non-trivial so we can
  // see what types and keys Gobodo actually returns (Person Account vs Partner Account vs Client).
  // Remove once diagnoses are confirmed.
  if (process.env.NODE_ENV === 'development' && (searchQuery?.trim().length ?? 0) > 0) {
    /* eslint-disable no-console */
    const summary = (result as Array<Record<string, unknown>>).map((r) => {
      const ot = (r as { objecttype?: { key?: string } }).objecttype;
      const data = (r as { data?: Record<string, unknown> }).data ?? {};
      return {
        key: (r as { key?: string }).key,
        objecttypekey: ot?.key,
        Name__c: (data as { Name__c?: unknown }).Name__c,
        FirstName__c: (data as { FirstName__c?: unknown }).FirstName__c,
        LastName__c: (data as { LastName__c?: unknown }).LastName__c,
      };
    });
    console.info(
      `[CB-investigation] traversal q="${searchQuery}" filter=${statusFilter ?? 'all'} startIndex=${startIndex ?? 0} count=${count ?? 0} → raw=${result.length} types=${Array.from(new Set(summary.map((s) => s.objecttypekey))).join(',')}`,
      summary
    );
    /* eslint-enable no-console */
  }

  const out: Record<string, unknown>[] = [];
  for (const gobodoObj of result) {
    const rec = convertFromGobodoRecord(gobodoObj, CLIENT_TYPE_KEY) as Record<string, unknown>;
    const key = (rec.key ?? rec.id) as string | undefined;
    if (key && String(key).endsWith(CUSTOM_SUFFIX)) {
      out.push(rec);
    }
  }
  return out;
}

/** Gobodo server-side cache for per-client row traversals (CreditBlock / Bureaus from Client). */
function rowEnrichmentTraversalCacheOpts(
  clientId: string,
  kind: 'credit-blocks' | 'bureaus',
  shape: 'full' | 'table' = 'full'
): GobodoCacheOpts {
  return makeCacheOpts(
    ['credit-block-row-traversal', kind, shape, stripSuffixForJoin(clientId)],
    CREDIT_BLOCK_CACHE_DURATION_SECONDS
  );
}

/**
 * CreditBlock → Client edges; from Client, traverse INCOMING to CreditBlock subscription records.
 * Mirrors case_client_rel pattern in creditBlockCaseQueries.
 */
export async function getCreditBlocksViaTraversalFromClient(
  clientId: string,
  opts?: { projectionDataFields?: string; cacheShape?: 'full' | 'table' }
): Promise<Record<string, unknown>[]> {
  if (!clientId) return [];
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(clientId);
  input.setRelationshipTypes([CREDIT_BLOCK_CLIENT_REL_KEY]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([CREDIT_BLOCK_QUERY_TYPE]);
  input.setMaxPathLength(1);
  const cacheOpts = rowEnrichmentTraversalCacheOpts(clientId, 'credit-blocks', opts?.cacheShape ?? 'full');
  const traversalOpts = opts?.projectionDataFields
    ? buildTraversalProjectionOpts(cacheOpts, opts.projectionDataFields)
    : cacheOpts;
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, traversalOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  if (!result || !Array.isArray(result)) return [];
  const out: Record<string, unknown>[] = [];
  for (const gobodoObj of result) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, CREDIT_BLOCK_QUERY_TYPE) as Record<string, unknown>;
      const key = (rec.key ?? rec.id) as string | undefined;
      if (key && String(key).endsWith(CUSTOM_SUFFIX)) out.push(rec);
    } catch {
      // skip malformed
    }
  }
  return out;
}

/** Credit_Bureaus → Client; from Client, traverse INCOMING to bureau rows. */
export async function getBureausViaTraversalFromClient(
  clientId: string,
  opts?: { projectionDataFields?: string; cacheShape?: 'full' | 'table' }
): Promise<Record<string, unknown>[]> {
  if (!clientId) return [];
  const apiClient = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(apiClient);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectKey(clientId);
  input.setRelationshipTypes([CREDIT_BUREAUS_CLIENT_REL_KEY]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([BUREAU_OBJECT]);
  input.setMaxPathLength(1);
  const cacheOpts = rowEnrichmentTraversalCacheOpts(clientId, 'bureaus', opts?.cacheShape ?? 'full');
  const traversalOpts = opts?.projectionDataFields
    ? buildTraversalProjectionOpts(cacheOpts, opts.projectionDataFields)
    : cacheOpts;
  const result = await retryWithBackoff(
    () => traversalApi.traverseObjects(input, traversalOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  if (!result || !Array.isArray(result)) return [];
  const out: Record<string, unknown>[] = [];
  for (const gobodoObj of result) {
    try {
      const rec = convertFromGobodoRecord(gobodoObj, BUREAU_OBJECT) as Record<string, unknown>;
      out.push(rec);
    } catch {
      // skip malformed
    }
  }
  return out;
}

const FULL_LIST_PAGE_SIZE = 200;
const FULL_LIST_MAX = 100;

/** Load all clients via traversal (for overview/stats/cases). Pages with FULL_LIST_PAGE_SIZE until a page returns fewer items or we hit FULL_LIST_MAX. */
async function getAllClientsViaTraversal(): Promise<Record<string, unknown>[]> {
  const accumulated: Record<string, unknown>[] = [];
  let startIndex = 0;
  while (startIndex < FULL_LIST_MAX) {
    const page = await getClientsViaTraversal(startIndex, FULL_LIST_PAGE_SIZE);
    for (const rec of page) accumulated.push(rec);
    if (page.length < FULL_LIST_PAGE_SIZE) break;
    startIndex += page.length;
  }
  return dedupeClientTraversalRecords(accumulated);
}

async function getDistinctClientTraversalKeys(statusFilter?: string, searchQuery?: string): Promise<string[]> {
  const COUNT_PAGE_SIZE = 200;
  const COUNT_MAX_RECORDS = 5000;
  const seen = new Set<string>();
  let startIndex = 0;

  while (startIndex < COUNT_MAX_RECORDS) {
    const page = await getClientsViaTraversalRaw(startIndex, COUNT_PAGE_SIZE, statusFilter, searchQuery, {
      objectSections: 'objectType',
      cacheShape: 'keys-only',
    });
    if (page.length === 0) break;
    for (const r of page) {
      const dedupeKey = getClientTraversalDedupeKey(r);
      if (dedupeKey) seen.add(dedupeKey);
    }
    if (page.length < COUNT_PAGE_SIZE) break;
    startIndex += COUNT_PAGE_SIZE;
  }

  return Array.from(seen);
}

async function getClientsTraversalCountNative(statusFilter?: string, searchQuery?: string): Promise<number> {
  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const input = buildCreditBlockTraversalInput(statusFilter, searchQuery);
  try {
    input.setShortestPath(true);
  } catch {
    // Backend/client may ignore shortestPath; fallback path below preserves correctness.
  }
  const cacheOpts = creditBlockClientTraversalCountCacheOpts(statusFilter, searchQuery);
  const raw = await retryWithBackoff(
    () => traversalApi.traverseObjectsCount(input, cacheOpts),
    { maxRetries: 2, initialDelayMs: 1000 }
  );
  return parseCreditBlockTraversalCount(raw);
}

/**
 * Return total count of DISTINCT clients reachable from CreditBlock -> Client traversal.
 *
 * Why: traverseObjectsCount returns a count of (CreditBlock, Client) traversal edges, not unique
 * Clients. A Client linked to multiple active CreditBlock subscriptions is counted multiple times,
 * inflating pagination (e.g. 23 pages displayed when only 12 pages of unique clients exist —
 * DEFECT-CB-098). Page rows are deduped via `dedupeClientTraversalRecords`, so the rendered totals
 * never agreed with the paginator.
 *
 * Approach: enumerate the same traversal in pages of COUNT_PAGE_SIZE, dedupe by canonical client
 * key (`getClientTraversalDedupeKey`), and return the unique count. Each page response is server-
 * side cached by `creditBlockClientTraversalCacheOpts`, so subsequent fetches are cheap.
 */
async function getClientsTraversalCount(statusFilter?: string, searchQuery?: string): Promise<number> {
  try {
    const nativeCount = await getClientsTraversalCountNative(statusFilter, searchQuery);
    if (nativeCount > 0) return nativeCount;
  } catch {
    // Fall back to exact key enumeration below.
  }
  try {
    return (await getDistinctClientTraversalKeys(statusFilter, searchQuery)).length;
  } catch {
    // count failure should not break the page
  }
  return 0;
}

/** Total client count from traversal API (traverseObjectsCount). Use for overview Total Clients card. */
export function useCreditBlockClientCount(enabled = true) {
  return useQuery({
    queryKey: creditBlockClientCountQueryKey(),
    queryFn: () => getClientsTraversalCount(),
    enabled: USE_GOBODO_API && enabled,
    staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
  });
}

/** Exact distinct client total for the Clients tab, keyed by status/search but loaded independently from row fetches. */
export function useCreditBlockClientsExactCount(
  statusFilter?: string,
  enabled = true,
  searchQuery?: string
) {
  const effectiveStatusFilter = statusFilter === 'all' || statusFilter == null ? undefined : statusFilter;
  const trimmedSearch = searchQuery?.trim() ?? '';
  const searchForApi = trimmedSearch || undefined;
  return useQuery({
    queryKey: creditBlockClientCountQueryKey(effectiveStatusFilter, searchForApi),
    queryFn: () => getClientsTraversalCount(effectiveStatusFilter, searchForApi),
    enabled: USE_GOBODO_API && enabled,
    staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
  });
}

const stripSuffixForJoin = (s: string) => s.replace(/__stg3_c$|__stg_c$|__dev_c$|__stg$|__c$/i, '');

/** Shared inclusion logic for Overview and Clients (fetch path). Ensures consistent counts. */
function buildClientIdsFromActiveCreditBlock(
  creditBlockRecords: Record<string, unknown>[],
  clientRecords: Record<string, unknown>[],
  strip: (s: string) => string
): Set<string> {
  const set = new Set<string>();
  for (const cb of creditBlockRecords) {
    if (!isCreditBlockActive(cb)) continue;
    const cid = getRelatedClientId(cb);
    if (cid) set.add(resolveToNormKey(cid, clientRecords, strip));
  }
  return set;
}

function includeCreditBlockClient(
  r: Record<string, unknown>,
  clientIdsFromActiveCreditBlock: Set<string>,
  strip: (s: string) => string
): boolean {
  const key = String((r as { key?: string }).key ?? r.id ?? '');
  const norm = strip(key);
  return hasCreditBlockProduct(r) || clientIdsFromActiveCreditBlock.has(norm);
}

/** Resolve client ref (key or numeric id) to canonical normKey for join. */
function resolveToNormKey(
  cid: string,
  clientRecords: Record<string, unknown>[],
  strip: (s: string) => string
): string {
  const norm = strip(cid);
  const byNorm = clientRecords.find((r) => strip(String((r as { key?: string }).key ?? r.id ?? '')) === norm);
  if (byNorm) return norm;
  const byId = clientRecords.find((r) => String(r.id ?? '') === cid || String((r as { key?: string }).key ?? '') === cid);
  if (byId) return strip(String((byId as { key?: string }).key ?? byId.id ?? ''));
  return norm;
}

/** Fetch credit blocks + bureaus and build join maps. Used by both traversal and fetch paths. */
async function fetchAndJoinCreditBlocksBureaus(
  clientRecords: Record<string, unknown>[]
): Promise<{ clientIdToCreditBlocks: Map<string, Record<string, unknown>[]>; clientIdToBureaus: Map<string, Record<string, unknown>[]> }> {
  const [creditBlockRecordsRaw, bureauRecordsRaw] = await Promise.all([
    getRecordsForObject(CREDIT_BLOCK_QUERY_TYPE, {
      size: CREDIT_BLOCK_BULK_LIST_SIZE,
      cache: CREDIT_BLOCK_CACHE_OPTS,
    }).catch(() => [] as Record<string, unknown>[]),
    getRecordsForObject(BUREAU_OBJECT, {
      size: CREDIT_BLOCK_BULK_LIST_SIZE,
      cache: BUREAU_CACHE_OPTS,
    }).catch(() => [] as Record<string, unknown>[]),
  ]);
  const creditBlockRecords = (creditBlockRecordsRaw as Record<string, unknown>[]).filter(
    (cb) => (cb as { key?: string }).key != null && String((cb as { key?: string }).key).endsWith(CUSTOM_SUFFIX)
  );
  const clientIdToCreditBlocks = new Map<string, Record<string, unknown>[]>();
  const clientIdToBureaus = new Map<string, Record<string, unknown>[]>();
  for (const cb of creditBlockRecords) {
    const cid = getRelatedClientId(cb);
    if (cid) {
      const norm = resolveToNormKey(cid, clientRecords, stripSuffixForJoin);
      const list = clientIdToCreditBlocks.get(norm) ?? [];
      list.push(cb);
      clientIdToCreditBlocks.set(norm, list);
    }
  }
  for (const b of bureauRecordsRaw as Record<string, unknown>[]) {
    const cid = getBureauClientId(b);
    if (cid) {
      const norm = resolveToNormKey(cid, clientRecords, stripSuffixForJoin);
      const list = clientIdToBureaus.get(norm) ?? [];
      list.push(b);
      clientIdToBureaus.set(norm, list);
    }
  }
  if (process.env.NODE_ENV === 'development' && bureauRecordsRaw.length > 0) {
    const matched = clientIdToBureaus.size;
    const totalBureaus = (bureauRecordsRaw as Record<string, unknown>[]).length;
    if (matched === 0) {
      const sample = bureauRecordsRaw[0] as Record<string, unknown>;
      const sampleCid = getBureauClientId(sample);
      const sampleKeys = Object.keys(sample).concat(Object.keys((sample.data as Record<string, unknown>) ?? {}).filter((k) => !(k in sample)));
      console.warn('[CreditBlock] Bureaus join: 0 matched. Sample bureau keys:', sampleKeys.slice(0, 20), '| clientId:', sampleCid);
    } else {
      console.log('[CreditBlock] Bureaus join:', matched, 'client(s) with bureaus,', totalBureaus, 'total bureau records');
    }
  }
  return { clientIdToCreditBlocks, clientIdToBureaus };
}

/** Raw Credit Block + Bureau records (fetched once and cached; join filters by clientIds in memory). */
export type CreditBlockJoinRawData = {
  creditBlockRecords: Record<string, unknown>[];
  bureauRecords: Record<string, unknown>[];
};

/** Fetch raw Credit Block and Bureau records (capped at CREDIT_BLOCK_BULK_LIST_SIZE each). Cached under CREDIT_BLOCK_JOIN_DATA_QUERY_KEY so the same request is not repeated for every page. */
export async function fetchRawCreditBlocksBureaus(): Promise<CreditBlockJoinRawData> {
  const [creditBlockRecordsRaw, bureauRecordsRaw] = await Promise.all([
    getRecordsForObject(CREDIT_BLOCK_QUERY_TYPE, {
      size: CREDIT_BLOCK_BULK_LIST_SIZE,
      cache: CREDIT_BLOCK_CACHE_OPTS,
    }).catch(() => [] as Record<string, unknown>[]),
    getRecordsForObject(BUREAU_OBJECT, {
      size: CREDIT_BLOCK_BULK_LIST_SIZE,
      cache: BUREAU_CACHE_OPTS,
    }).catch(() => [] as Record<string, unknown>[]),
  ]);
  const creditBlockRecords = (creditBlockRecordsRaw as Record<string, unknown>[]).filter(
    (cb) => (cb as { key?: string }).key != null && String((cb as { key?: string }).key).endsWith(CUSTOM_SUFFIX)
  );
  return {
    creditBlockRecords,
    bureauRecords: bureauRecordsRaw as Record<string, unknown>[],
  };
}

/** Build join maps from raw CB/Bureau data filtered to the given client IDs (normed). */
function buildJoinMapsFromRaw(
  raw: CreditBlockJoinRawData,
  clientIds: string[]
): { clientIdToCreditBlocks: Map<string, Record<string, unknown>[]>; clientIdToBureaus: Map<string, Record<string, unknown>[]> } {
  const normSet = new Set(clientIds.map(stripSuffixForJoin).filter(Boolean));
  if (normSet.size === 0) {
    return { clientIdToCreditBlocks: new Map(), clientIdToBureaus: new Map() };
  }
  const clientIdToCreditBlocks = new Map<string, Record<string, unknown>[]>();
  const clientIdToBureaus = new Map<string, Record<string, unknown>[]>();
  for (const cb of raw.creditBlockRecords) {
    const cid = getRelatedClientId(cb);
    if (cid) {
      const norm = stripSuffixForJoin(cid);
      if (normSet.has(norm)) {
        const list = clientIdToCreditBlocks.get(norm) ?? [];
        list.push(cb);
        clientIdToCreditBlocks.set(norm, list);
      }
    }
  }
  for (const b of raw.bureauRecords) {
    const cid = getBureauClientId(b);
    if (cid) {
      const norm = stripSuffixForJoin(cid);
      if (normSet.has(norm)) {
        const list = clientIdToBureaus.get(norm) ?? [];
        list.push(b);
        clientIdToBureaus.set(norm, list);
      }
    }
  }
  return { clientIdToCreditBlocks, clientIdToBureaus };
}

/** Normalized client id for join map lookups. Export so UI can use the same key when merging. */
export function normClientIdForJoin(clientId: string): string {
  return stripSuffixForJoin(clientId);
}

/** Lazy-load join data (Credit Block + Bureau) for the given client IDs. Uses cached raw CB/Bureau data (single fetch) and filters in memory so the same API requests are not repeated for every page. */
export function useCreditBlockClientsJoin(clientIds: string[], enabled = true) {
  const sortedKey = [...clientIds].map(stripSuffixForJoin).filter(Boolean).sort().join(',');
  return useQuery({
    queryKey: ['credit-block', 'clients', 'join', sortedKey, CUSTOM_SUFFIX],
    queryFn: async ({ client }) => {
      const raw = await client.ensureQueryData({
        queryKey: [...CREDIT_BLOCK_JOIN_DATA_QUERY_KEY],
        queryFn: fetchRawCreditBlocksBureaus,
        staleTime: 60_000,
      });
      return buildJoinMapsFromRaw(raw, clientIds);
    },
    enabled: clientIds.length > 0 && enabled,
    staleTime: 60_000,
  });
}

/** Per-client Credit Block + Bureau slice after join (for table rows). */
export type CreditBlockClientJoinRowData = {
  creditBlocks: Record<string, unknown>[];
  bureaus: Record<string, unknown>[];
};

/**
 * One TanStack Query per visible client row: parallel Graph traversals Client → CreditBlock and Client → Bureaus.
 * Gobodo cache keys are per client + kind; TanStack caches the merged row slice by normalized client id.
 */
export function useCreditBlockClientsJoinQueries(clientIds: string[], enabled = true) {
  return useQueries({
    queries: clientIds.map((clientId) => {
      const norm = stripSuffixForJoin(clientId);
      return {
        queryKey: ['credit-block', 'clients', 'row-traversal', norm, CUSTOM_SUFFIX] as const,
        queryFn: async (): Promise<CreditBlockClientJoinRowData> => {
          const [creditBlocks, bureaus] = await Promise.all([
            getCreditBlocksViaTraversalFromClient(clientId, {
              projectionDataFields: CREDIT_BLOCK_TABLE_ROW_DATA_FIELDS,
              cacheShape: 'table',
            }),
            getBureausViaTraversalFromClient(clientId, {
              projectionDataFields: BUREAU_TABLE_ROW_DATA_FIELDS,
              cacheShape: 'table',
            }),
          ]);
          return { creditBlocks, bureaus };
        },
        enabled: USE_GOBODO_API && !!clientId && enabled,
        staleTime: 60_000,
        retry: (failureCount: number, error: unknown) => failureCount < 1 && isRetryableError(error),
      };
    }),
  });
}

/** Merge join data (credit blocks + bureaus) into a skeleton client for display. Reuses same logic as mapToCreditBlockClient for bureauCredentials and assignedProducts. */
export function mergeJoinIntoClient(
  client: CreditBlockClient,
  creditBlocksForClient: Record<string, unknown>[],
  bureausForClient: Record<string, unknown>[]
): CreditBlockClient {
  const fromCreditBlocks: CreditBlockClient['assignedProducts'] = creditBlocksForClient.map((cb, i) =>
    mapCreditBlockRecordToProduct(cb, i)
  );
  const assignedProducts = fromCreditBlocks.length > 0 ? fromCreditBlocks : (client.assignedProducts ?? []);
  let bureauCredentials: CreditBlockClient['bureauCredentials'] = client.bureauCredentials ?? {
    chexsystems: {},
    equifax: {},
    experian: {},
    transunion: {},
  };
  if (bureausForClient.length > 0) {
    try {
      bureauCredentials = {
        chexsystems: {},
        equifax: {},
        experian: {},
        transunion: {},
      };
      for (const b of bureausForClient) {
        const bureau = normalizeBureauName(b.Bureau_Name__c ?? b.Bureau__c ?? b.bureau ?? b.Name__c ?? b.name);
        bureauCredentials[bureau] = mapBureauRecordToCredential(b);
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[CreditBlock] mergeJoinIntoClient bureauCredentials:', e);
    }
  }
  return { ...client, bureauCredentials, assignedProducts };
}

/** Default cap for traversal-backed client picker autocomplete (New case modal). */
const CLIENT_PICKER_TRAVERSAL_MAX = 25;

/**
 * New case modal / autocomplete: CreditBlock→Client traversal + `GraphTraversalInput` search (same mechanism as the Clients tab).
 * On traversal failure, returns [] (no bulk Client__* list fetch).
 */
export async function searchCreditBlockClientsViaTraversal(
  searchQuery: string,
  maxResults: number = CLIENT_PICKER_TRAVERSAL_MAX
): Promise<CreditBlockClient[]> {
  if (!USE_GOBODO_API) return [];
  const q = searchQuery.trim();
  if (!q) return [];
  try {
    const clientRecs = await getClientsViaTraversal(0, maxResults, undefined, q, {
      projectionDataFields: CLIENT_LIST_TRAVERSAL_DATA_FIELDS,
      cacheShape: 'table',
    });
    const clients: CreditBlockClient[] = [];
    for (const r of clientRecs) {
      try {
        clients.push(mapToCreditBlockClient(r, [], []));
      } catch {
        /* skip malformed row */
      }
    }
    return clients;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[CreditBlock] Client picker traversal search failed (no Client bulk fallback).', e);
    }
    return [];
  }
}

export function useRecentlyViewedCreditBlockClients(lookups: string[], enableQuery = true) {
  const orderedLookups = lookups.map((lookup) => String(lookup ?? '').trim()).filter(Boolean);
  const orderedKey = orderedLookups.join(',');
  return useQuery({
    queryKey: ['credit-block', 'clients', 'recently-viewed', orderedKey, CUSTOM_SUFFIX],
    queryFn: async (): Promise<CreditBlockClient[]> => {
      const settled = await Promise.allSettled(
        orderedLookups.map((lookup) => fetchCreditBlockClientListRowByLookup(lookup))
      );
      const clients = settled
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter((client): client is CreditBlockClient => Boolean(client?.id));
      await resolveUserNamesOnClients(clients);
      return clients;
    },
    enabled: USE_GOBODO_API && enableQuery && orderedLookups.length > 0,
    staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
    retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
  });
}

export interface CreditBlockClientsPaginatedResult {
  clients: CreditBlockClient[];
  totalCount: number;
}

/**
 * Server-side paginated Credit Block clients via traversal (startIndex/count).
 * Optional statusFilter (active/suspended/cancelled/inactive) is passed to the traversal so the API returns only matching clients.
 * Optional searchQuery is sent as GraphTraversalInput search (same graph id) so results are server-filtered, not page-limited client-side.
 * Use this for the clients tab when you want pagination without loading all clients.
 * Exact distinct totals are loaded separately by {@link useCreditBlockClientsExactCount} so row rendering does not wait on key-only count enumeration.
 */
export function useCreditBlockClientsPaginated(
  page: number,
  pageSize: number,
  statusFilter?: string,
  enableQuery = true,
  searchQuery?: string
) {
  const queryClient = useQueryClient();
  const startIndex = (page - 1) * pageSize;
  const shouldFetchExactCount = page <= 1;
  const inferTotalCountFromPage = (pageRows: CreditBlockClient[]): number => {
    const safeStart = Math.max(0, startIndex);
    if (pageRows.length === 0) return safeStart;
    return safeStart + pageRows.length + (pageRows.length === pageSize ? 1 : 0);
  };
  const effectiveStatusFilter = statusFilter === 'all' || statusFilter == null ? undefined : statusFilter;
  const trimmedSearch = searchQuery?.trim() ?? '';
  const searchSegment = trimmedSearch ? `q:${trimmedSearch}` : 'no-q';
  const searchForApi = trimmedSearch || undefined;
  const countQueryKey = creditBlockClientCountQueryKey(effectiveStatusFilter, searchForApi);
  return useQuery({
    queryKey: ['credit-block', 'clients', 'paginated', page, pageSize, statusFilter ?? 'all', searchSegment, CUSTOM_SUFFIX],
    queryFn: async (): Promise<CreditBlockClientsPaginatedResult> => {
      const runFallback = async (): Promise<CreditBlockClientsPaginatedResult> => {
        const records = await getRecordsForObject(CLIENT_OBJECT, {
          size: CREDIT_BLOCK_BULK_LIST_SIZE,
          cache: CLIENT_CACHE_OPTS,
        }).catch(() => [] as Record<string, unknown>[]);
        let clients = (records as Record<string, unknown>[])
          .map((r) => mapToCreditBlockClient(r, [], []))
          .filter((c) => !!(c.id && String(c.id).trim()));

        if (effectiveStatusFilter) {
          clients = clients.filter((c) => String(c.subscriptionStatus ?? '').toLowerCase() === effectiveStatusFilter);
        }

        if (trimmedSearch) {
          const q = trimmedSearch.toLowerCase();
          clients = clients.filter((c) => {
            const name = String(c.accountName ?? '').toLowerCase();
            const email = String(c.contact?.email ?? '').toLowerCase();
            const owner = String(c.accountOwner ?? '').toLowerCase();
            const id = String(c.id ?? '').toLowerCase();
            return name.includes(q) || email.includes(q) || owner.includes(q) || id.includes(q);
          });
        }
        const totalCount = clients.length;
        const start = Math.max(0, startIndex);
        const page = clients.slice(start, start + pageSize);
        await resolveUserNamesOnClients(page);
        await resolveAccountNamesOnClients(page);
        return { clients: page, totalCount };
      };

      try {
        const clientRecs = await getClientsViaTraversalRaw(
          startIndex,
          pageSize,
          effectiveStatusFilter,
          searchForApi,
          {
            projectionDataFields: CLIENT_LIST_TRAVERSAL_DATA_FIELDS,
            cacheShape: 'table',
          }
        );
        const cachedCount = queryClient.getQueryData<number>(countQueryKey);
        if (typeof cachedCount !== 'number') {
          void queryClient.prefetchQuery({
            queryKey: countQueryKey,
            queryFn: () => getClientsTraversalCount(effectiveStatusFilter, searchForApi),
            staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
            retry: (failureCount, error) => failureCount < 1 && isRetryableError(error),
          }).catch(() => undefined);
        }
        // Traversal-only: do not bulk-fetch all Client__* via getObjectsInGraphByType (credit-block-client-objects).
        const clients: CreditBlockClient[] = [];
        for (const r of clientRecs) {
          try {
            clients.push(mapToCreditBlockClient(r, [], []));
          } catch (rowErr) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CreditBlock] paginated traversal row map skip', rowErr);
            }
          }
        }
        await resolveUserNamesOnClients(clients);
        if (typeof cachedCount === 'number' && cachedCount > 0) {
          return { clients, totalCount: cachedCount };
        }
        if (typeof cachedCount === 'number' && cachedCount === 0 && shouldFetchExactCount) {
          return { clients, totalCount: 0 };
        }
        if (clients.length === 0 && shouldFetchExactCount) {
          return { clients, totalCount: 0 };
        }
        return { clients, totalCount: inferTotalCountFromPage(clients) };
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CreditBlock] paginated clients traversal failed (no Client bulk fallback)', e);
        }
        return runFallback();
      }
    },
    enabled: USE_GOBODO_API && page >= 1 && pageSize >= 1 && enableQuery,
    staleTime: CREDIT_BLOCK_QUERY_STALE_TIME_MS,
    throwOnError: false,
  });
}

export interface CreditBlockStats {
  totalClients: number;
  activeClients: number;
  suspendedClients: number;
  totalProtectedBureaus: number;
  pendingTasks: number;
  recentAlerts: Array<{ id: string; clientName: string; message: string; bureau: string; date: string; severity: string }>;
}

export function useCreditBlockStats(
  clients: Array<{ subscriptionStatus: string; tasks?: Array<{ status: string }>; bureauCredentials?: Record<string, unknown> }>,
  fallback: CreditBlockStats
): CreditBlockStats {
  if (!clients.length) {
    return {
      totalClients: 0,
      activeClients: 0,
      suspendedClients: 0,
      totalProtectedBureaus: 0,
      pendingTasks: 0,
      recentAlerts: [],
    };
  }
  const activeClients = clients.filter((c) => c.subscriptionStatus === 'active').length;
  const suspendedClients = clients.filter((c) => c.subscriptionStatus === 'suspended').length;
  const totalProtectedBureaus = clients.reduce((acc, c) => {
    const bc = c.bureauCredentials as Record<string, { pin?: string }> | undefined;
    if (!bc) return acc;
    let n = 0;
    ['chexsystems', 'equifax', 'experian', 'transunion'].forEach((b) => {
      if (bc[b]?.pin) n++;
    });
    return acc + n;
  }, 0);
  const pendingTasks = clients.reduce(
    (acc, c) =>
      acc + (c.tasks?.filter((t) => t.status === 'pending' || t.status === 'in_progress').length ?? 0),
    0
  );
  return {
    totalClients: clients.length,
    activeClients,
    suspendedClients,
    totalProtectedBureaus,
    pendingTasks,
    recentAlerts: fallback.recentAlerts,
  };
}

/** Update long text on Client{suffix} (default field Description__c). */
export function useUpdateCreditBlockClientNotes(clientId: string) {
  const queryClient = useQueryClient();
  const field = process.env.REACT_APP_CREDIT_BLOCK_CLIENT_NOTES_FIELD || 'Description__c';
  return useMutation({
    mutationFn: async (notes: string) => {
      await updateRecord(CLIENT_OBJECT, clientId, { [field]: notes }, { gobodoPropertyPatch: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-block'] });
    },
  });
}
